import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import Button from '../../components/ui/Button';
import AuthShell from '../../components/auth/AuthShell';
import { LANGUAGES } from '../../i18n/LanguageContext';
import { slides, PRODUCT_LABEL } from '../../components/auth/slides';

const FIELD_STYLE = {
  width: '100%',
  padding: '10px 14px',
  border: '0.5px solid var(--color-border-light)',
  borderRadius: 'var(--radius-md)',
  fontSize: 13,
  background: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
  outline: 'none',
};

const LABEL_STYLE = {
  fontSize: 11,
  color: 'var(--color-text-secondary)',
  display: 'block',
  marginBottom: 6,
};

export default function Signup() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [preferredLanguage, setPreferredLanguage] = useState('en');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isValid = email.trim() && fullName.trim() && orgName.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setError('');
    setSubmitting(true);
    try {
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      const result = await createCheckoutSession({
        email: email.trim(),
        fullName: fullName.trim(),
        orgName: orgName.trim(),
        preferredLanguage,
        baseUrl: window.location.origin,
      });
      const url = result.data?.url;
      if (!url) throw new Error('No checkout URL returned.');
      window.location.href = url;
    } catch (err) {
      console.error('Signup error', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <AuthShell productLabel={PRODUCT_LABEL} slides={slides}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0, marginBottom: 6 }}>Create your account</h1>
          <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.5 }}>
            Agriware Operations · £50/month · 10 machines and unlimited users · Cancel anytime
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label style={LABEL_STYLE}>Full name</label>
            <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} style={FIELD_STYLE} required autoFocus />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={LABEL_STYLE}>Work email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={FIELD_STYLE} required />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={LABEL_STYLE}>Organisation name</label>
            <input type="text" value={orgName} onChange={e => setOrgName(e.target.value)} style={FIELD_STYLE} required placeholder="e.g. Smith Farms Ltd" />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={LABEL_STYLE}>Preferred language</label>
            <select value={preferredLanguage} onChange={e => setPreferredLanguage(e.target.value)} style={FIELD_STYLE}>
              {LANGUAGES.map(l => (<option key={l.code} value={l.code}>{l.label}</option>))}
            </select>
          </div>

          {error && (
            <div style={{ marginBottom: 14, padding: '10px 12px', background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-sm)', fontSize: 12 }}>
              {error}
            </div>
          )}

          <Button type="submit" variant="primary" disabled={!isValid || submitting} loading={submitting} style={{ width: '100%' }}>
            {submitting ? 'Redirecting to checkout…' : 'Continue to payment'}
          </Button>

          <div style={{ marginTop: 20, fontSize: 11, color: 'var(--color-text-tertiary)', lineHeight: 1.6, textAlign: 'center' }}>
            By creating an account you agree to our terms of service. Your subscription is processed by Stripe. You can cancel at any time from your billing settings.
          </div>

          <div style={{ marginTop: 16, textAlign: 'center', fontSize: 12, color: 'var(--color-text-secondary)' }}>
            Already have an account? <a href="/login" style={{ color: 'var(--color-brand)', textDecoration: 'none' }}>Sign in</a>
          </div>
        </form>
    </AuthShell>
  );
}
